import { groupBy } from 'lodash';
import { useWatch } from 'react-hook-form';

import { useSankeyFormContext } from 'contexts/sankeyFormContext';

import { renderConfig } from '../../constants';
import { useSankeyRenderContext } from '../../contexts/sankeyRenderContext';
import { useSankeySVGInteraction } from '../SankeyInteractionProvider/utils';
import SankeyDraggedNodeLines from './SankeyDraggedNodeLines';
import SankeyNodesAndFlows from './SankeyNodesAndFlows';
import SankeyTitle from './SankeyTitle';
import SankeyWatermark from './SankeyWatermark';

const SankeySVG: React.FC = () => {
  const { graph, flows, nodes, diagramTitle } = useSankeyRenderContext();
  const { form } = useSankeyFormContext();
  const {
    draggedNode,
    isDiagramInteractive,
    isElementFaded,
    handleMouseOver,
    handleMouseMove,
    handleMouseLeave,
    handleMouseUp,
  } = useSankeySVGInteraction();

  // Sort flows to be rendered from largest to smallest
  // so if flows cross, the smaller ones are drawn on top
  const sortedFlows = [...flows].sort((a, b) => b.dy - a.dy);

  const { true: fadedFlows = [], false: regularFlows = [] } = groupBy(
    sortedFlows,
    (flow) => isElementFaded({ type: 'flow', id: flow.id })
  );
  const { true: fadedNodes = [], false: regularNodes = [] } = groupBy(
    nodes,
    (node) => isElementFaded({ type: 'node', id: node.id })
  );

  const [
    backgroundColor,
    canvasWidth = 0,
    canvasHeight = 0,
    fontFamily,
    showTitle,
    showWatermark,
  ] = useWatch({
    control: form.control,
    name: [
      'background_color',
      'canvas_width',
      'canvas_height',
      'font_family',
      'show_title',
      'show_watermark',
    ],
  });

  return (
    <svg
      id="sankey_svg"
      xmlns="http://www.w3.org/2000/svg"
      data-title={diagramTitle}
      fontFamily={fontFamily}
      fontWeight={renderConfig.font.weight.normal}
      onMouseOver={handleMouseOver}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseUp={handleMouseUp}
      width={canvasWidth}
      height={canvasHeight}
      viewBox={`0 0 ${canvasWidth} ${canvasHeight}`}
      style={{ width: '100%', height: 'auto' }} // Stretch the SVG to the parent container
    >
      <rect
        id="sankey_background"
        fill={backgroundColor}
        width={canvasWidth}
        height={canvasHeight}
        x="0"
        y="0"
      />
      <g
        id="sankey_content"
        // moves the remaining diagram inward based on the user's margins
        transform={`translate(${graph.leftMargin},${renderConfig.canvasPadding.top})`}
      >
        {/* Helper layer */}
        {draggedNode && <SankeyDraggedNodeLines node={draggedNode} />}
        {/* Faded nodes and flows (for interactive diagrams in the edit mode) */}
        {isDiagramInteractive && (
          <SankeyNodesAndFlows
            flows={fadedFlows}
            nodes={fadedNodes}
            opacity={renderConfig.fadeOutOpacity}
          />
        )}
        {/* Regular nodes and flows */}
        <SankeyNodesAndFlows flows={regularFlows} nodes={regularNodes} />
        {/* Diagram title */}
        {showTitle && <SankeyTitle />}
        {/* Watermark */}
        {showWatermark && <SankeyWatermark />}
      </g>
    </svg>
  );
};

export default SankeySVG;
